/**
 * MQTT Connection Manager for AWS IoT Core
 *
 * MQTT-over-WebSocket transport using mqtt.js.
 * Authenticates via AWS IoT Core Custom Authorizer (shyft-mqtt-authorizer).
 *
 * Connection flow:
 *   1. Fetch credentials from GET /api/mqtt/credentials
 *   2. Connect to wss://<endpoint>/mqtt with Custom Authorizer params
 *   3. Lambda validates token via Rails API
 *   4. IoT Core grants connection scoped to user's topic
 *
 * @see shyft_api_server/lib/lambda/mqtt_authorizer/index.js
 * @see shyft_api_server/app/controllers/api/mqtt_controller.rb
 */

import mqtt from 'mqtt';
import { protectedAxiosInstance } from 'helpers/api-calls';

// Connection states
export const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
};

// AWS IoT Custom Authorizer name (matches deployment config)
const CUSTOM_AUTHORIZER_NAME = 'shyft-mqtt-authorizer';

// Reconnection config
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

// Token refresh interval (1 hour, matching Lambda refreshAfterInSeconds: 3600)
const TOKEN_REFRESH_MS = 3600 * 1000;

/**
 * Creates an MQTT connection manager instance.
 *
 * Usage:
 *   const manager = createMqttConnectionManager();
 *   manager.connect();
 *   // later...
 *   manager.disconnect();
 *
 * @returns {Object} Connection manager API
 */
export function createMqttConnectionManager() {
  let client = null;
  let credentials = null;
  let connectionState = ConnectionState.DISCONNECTED;
  let retryCount = 0;
  let reconnectTimer = null;
  let tokenRefreshTimer = null;
  let isDisconnecting = false;
  let stateListeners = [];

  /**
   * Notify all state listeners of a connection state change.
   */
  function setConnectionState(newState) {
    connectionState = newState;
    stateListeners.forEach((listener) => {
      try {
        listener(newState);
      } catch (e) {
        // Don't let listener errors break the manager
        console.error('[MqttManager] State listener error:', e);
      }
    });
  }

  /**
   * Fetch MQTT credentials from the backend API.
   * Response shape: { endpoint, topic, token, uuid, region }
   */
  async function fetchCredentials() {
    const response = await protectedAxiosInstance.request({
      url: '/api/mqtt/credentials',
      method: 'GET',
    });
    return response.data;
  }

  /**
   * Calculate exponential backoff delay.
   * Formula: min(1000 * 2^retryCount, 30000)
   */
  function getBackoffDelay() {
    return Math.min(INITIAL_BACKOFF_MS * Math.pow(2, retryCount), MAX_BACKOFF_MS);
  }

  /**
   * Schedule a reconnection attempt with exponential backoff.
   */
  function scheduleReconnect() {
    if (isDisconnecting) return;

    clearTimeout(reconnectTimer);
    const delay = getBackoffDelay();
    console.log(`[MqttManager] Scheduling reconnect in ${delay}ms (attempt ${retryCount + 1})`);

    reconnectTimer = setTimeout(() => {
      retryCount++;
      connectInternal();
    }, delay);
  }

  /**
   * Schedule a token refresh before expiry.
   */
  function scheduleTokenRefresh() {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = setTimeout(() => {
      console.log('[MqttManager] Token refresh triggered');
      refreshCredentialsAndReconnect();
    }, TOKEN_REFRESH_MS);
  }

  /**
   * Refresh credentials and reconnect with new token.
   */
  async function refreshCredentialsAndReconnect() {
    if (isDisconnecting) return;

    try {
      // Cleanly close the existing connection
      if (client) {
        client.end(true);
        client = null;
      }

      // Fetch fresh credentials and reconnect
      retryCount = 0;
      await connectInternal();
    } catch (err) {
      console.error('[MqttManager] Token refresh failed:', err);
      setConnectionState(ConnectionState.ERROR);
      scheduleReconnect();
    }
  }

  /**
   * Internal connect logic - fetches credentials and establishes MQTT connection.
   */
  async function connectInternal() {
    if (isDisconnecting) return;

    setConnectionState(ConnectionState.CONNECTING);

    try {
      credentials = await fetchCredentials();

      const { endpoint, token, uuid } = credentials;

      // Build WebSocket URL with Custom Authorizer query params
      // The Custom Authorizer Lambda extracts credentials from:
      //   - MQTT username field -> uuid
      //   - MQTT password field -> token (API key)
      // Additionally, we pass authorizer name in the URL query string.
      const wsUrl = `wss://${endpoint}/mqtt?x-amz-customauthorizer-name=${encodeURIComponent(CUSTOM_AUTHORIZER_NAME)}`;

      client = mqtt.connect(wsUrl, {
        protocolVersion: 4,
        // Custom Authorizer credentials via MQTT CONNECT packet
        username: uuid,
        password: token,
        // Client ID must be unique per connection
        clientId: `dashboard_${uuid}_${Date.now()}`,
        // Disable mqtt.js built-in reconnect - we handle it ourselves
        reconnectPeriod: 0,
        // Connection timeout
        connectTimeout: 10000,
        // Clean session
        clean: true,
      });

      // --- Event Handlers ---

      client.on('connect', () => {
        console.log('[MqttManager] Connected to IoT Core');
        retryCount = 0;
        setConnectionState(ConnectionState.CONNECTED);
        scheduleTokenRefresh();
      });

      client.on('error', (err) => {
        console.error('[MqttManager] Connection error:', err);

        // Auth errors (4 = bad username/password in CONNACK) trigger credential refresh
        if (err.code === 4 || err.code === 5 || (err.message && err.message.includes('Not authorized'))) {
          console.log('[MqttManager] Auth error detected, refreshing credentials');
          refreshCredentialsAndReconnect();
          return;
        }

        setConnectionState(ConnectionState.ERROR);
        scheduleReconnect();
      });

      client.on('close', () => {
        if (!isDisconnecting) {
          console.log('[MqttManager] Connection closed unexpectedly');
          setConnectionState(ConnectionState.DISCONNECTED);
          scheduleReconnect();
        }
      });

      client.on('offline', () => {
        if (!isDisconnecting) {
          console.log('[MqttManager] Client went offline');
          setConnectionState(ConnectionState.DISCONNECTED);
        }
      });

      client.on('reconnect', () => {
        // Should not fire since reconnectPeriod is 0, but handle defensively
        console.log('[MqttManager] Reconnecting...');
        setConnectionState(ConnectionState.CONNECTING);
      });

    } catch (err) {
      console.error('[MqttManager] Connect failed:', err);
      setConnectionState(ConnectionState.ERROR);
      scheduleReconnect();
    }
  }

  /**
   * Handle page unload - clean disconnect.
   */
  function handleBeforeUnload() {
    disconnect();
  }

  // --- Public API ---

  /**
   * Start the MQTT connection.
   */
  function connect() {
    isDisconnecting = false;
    retryCount = 0;
    window.addEventListener('beforeunload', handleBeforeUnload);
    connectInternal();
  }

  /**
   * Cleanly disconnect and stop all reconnection/refresh timers.
   */
  function disconnect() {
    isDisconnecting = true;
    clearTimeout(reconnectTimer);
    clearTimeout(tokenRefreshTimer);
    reconnectTimer = null;
    tokenRefreshTimer = null;
    window.removeEventListener('beforeunload', handleBeforeUnload);

    if (client) {
      try {
        client.end(true);
      } catch (e) {
        // Ignore errors during disconnect
      }
      client = null;
    }

    credentials = null;
    retryCount = 0;
    setConnectionState(ConnectionState.DISCONNECTED);
  }

  /**
   * Get the current MQTT client instance.
   * May be null if not connected.
   *
   * @returns {Object|null} mqtt.js client
   */
  function getClient() {
    return client;
  }

  /**
   * Get the current connection state.
   *
   * @returns {string} One of ConnectionState values
   */
  function getConnectionState() {
    return connectionState;
  }

  /**
   * Get the current credentials (topic, uuid, etc.).
   *
   * @returns {Object|null} Credentials from /api/mqtt/credentials
   */
  function getCredentials() {
    return credentials;
  }

  /**
   * Subscribe to a topic.
   *
   * @param {string} topic - MQTT topic to subscribe to
   * @param {Object} [opts] - mqtt.js subscribe options
   * @param {Function} [callback] - Optional callback
   */
  function subscribe(topic, opts, callback) {
    if (client && connectionState === ConnectionState.CONNECTED) {
      client.subscribe(topic, opts, callback);
    } else {
      console.warn('[MqttManager] Cannot subscribe - not connected');
    }
  }

  /**
   * Unsubscribe from a topic.
   *
   * @param {string} topic - MQTT topic to unsubscribe from
   * @param {Object} [opts] - mqtt.js unsubscribe options
   * @param {Function} [callback] - Optional callback
   */
  function unsubscribe(topic, opts, callback) {
    if (client) {
      client.unsubscribe(topic, opts, callback);
    }
  }

  /**
   * Register a listener for connection state changes.
   *
   * @param {Function} listener - Called with (newState) on each state change
   * @returns {Function} Unsubscribe function
   */
  function onStateChange(listener) {
    stateListeners.push(listener);
    return () => {
      stateListeners = stateListeners.filter((l) => l !== listener);
    };
  }

  return {
    connect,
    disconnect,
    getClient,
    getConnectionState,
    getCredentials,
    subscribe,
    unsubscribe,
    onStateChange,
  };
}

export default createMqttConnectionManager;
